import { traced, SpanKind } from './appInsights';

type Method = 'get' | 'post' | 'put' | 'delete' | 'patch';

export interface RequestParameters {
  method?: Method;
  body?: unknown;
  headers?: Record<string, string>;
  signal?: AbortSignal;
  /** Override the dependency-span target host. Defaults to the current page host. */
  target?: string;
  /** Override the dependency-span name. Defaults to `${METHOD} ${path}`; use a
   * low-cardinality route template (e.g. `'GET /users/:id'`) when `path` contains
   * variable segments. */
  traceName?: string;
}

export interface Response<T = unknown> {
  success: boolean;
  body?: T;
  status: number;
}

/**
 * Send an HTTP request with W3C trace-context propagation.
 *
 * Defaults are tuned for calls to the local Next.js API (relative `path`,
 * span target = current host). Pass `target` and `traceName` to call a
 * cross-origin API while keeping span attribution and cardinality correct.
 */
export async function sendRequest<T = unknown>(
  path: string,
  requestParameters?: RequestParameters
): Promise<Response<T>> {
  const params = requestParameters ?? {};
  const method = params.method ?? 'get';
  const body = params.body ? JSON.stringify(params.body) : null;

  return traced(
    params.traceName ?? `${method.toUpperCase()} ${path}`,
    { kind: SpanKind.CLIENT, target: params.target ?? window.location.host },
    async (ctx) => {
      const response = await fetch(path, {
        method,
        headers: {
          'Content-Type': 'application/json',
          traceparent: ctx.traceparent,
          ...params.headers,
        },
        body,
        signal: params.signal,
      });

      const responseBody = await response.text();

      return {
        success: response.ok,
        body: responseBody.length > 0 ? JSON.parse(responseBody) : null,
        status: response.status,
      };
    }
  );
}
